import React, { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react'
import "../css/jp_Sidebar.css"
import { DropDownIcon } from '../../../../assets-module/asset-configuration/icon/iconExporter/iconExporter'

import { DepartmentItem, getDepartmentListApi, getJobLevelIndexApi, JobLevelItem } from './jb.type'

type Props = {
  filters: Record<string, Set<string>>
  onChangeFilters: Dispatch<SetStateAction<Record<string, Set<string>>>>
}

type MenuFilterItem = { slug: string; filter_title: string }
type MenuNavItem = { id: number; title: string; field: string; filter: MenuFilterItem[] }

const JPSidebar: FC<Props> = ({ filters, onChangeFilters }) => {
  const [openMenuIds, setOpenMenuIds] = useState<Set<number>>(new Set())
  const [departments, setDepartments] = useState<DepartmentItem[]>([])
  const [jobLevels, setJobLevels] = useState<JobLevelItem[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let alive = true

    const run = async () => {
      try {
        setLoading(true)

        const [deptRes, levelRes] = await Promise.all([
          getDepartmentListApi(),
          getJobLevelIndexApi(),
        ])

        if (!alive) return

        const deptRaw = deptRes.data.data.items ?? []
        const deptMap = new Map<string, DepartmentItem>()
        deptRaw
          .filter(d => d.isActive)
          .forEach(d => {
            if (!deptMap.has(d.slug)) deptMap.set(d.slug, d)
          })
        setDepartments(Array.from(deptMap.values()))

        const levelRaw = levelRes.data.data.data.items ?? []
        const levelMap = new Map<string, JobLevelItem>()
        levelRaw
          .filter(l => l.isActive)
          .forEach(l => {
            if (!levelMap.has(l.name)) levelMap.set(l.name, l)
          })
        setJobLevels(Array.from(levelMap.values()).sort((a, b) => a.rank - b.rank))
      } finally {
        if (!alive) return //NOSONAR
        setLoading(false)
      }
    }

    run()
    return () => { alive = false }
  }, [])

  const menuNavData: MenuNavItem[] = useMemo(() => {
    const deptItems: MenuFilterItem[] = [
      { slug: 'all', filter_title: 'All' },
      ...departments.map(d => ({ slug: d.name, filter_title: d.name })),
    ]

    const levelItems: MenuFilterItem[] = [
      { slug: 'all', filter_title: 'All' },
      ...jobLevels.map(l => ({ slug: l.name, filter_title: l.name })),
    ]

    return [
      { id: 1, title: 'Department', field: 'department', filter: deptItems },
      { id: 2, title: 'Level', field: 'level', filter: levelItems },
    ]
  }, [departments, jobLevels])

  const onClickNavMenu = (id: number) => {
    setOpenMenuIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const onToggle = (field: string, slug: string) => {
    onChangeFilters(prev => {
      if (slug === 'all') return { ...prev, [field]: new Set() }

      const next = new Set(prev[field] ?? new Set<string>())
      next.has(slug) ? next.delete(slug) : next.add(slug)

      return { ...prev, [field]: next }
    })
  }

  return (
    <aside className='scl--as-car-con-st'>
      <nav className='scl--accs-na-bo'>
        {loading ? (
          <div style={{ padding: 12 }}>Loading…</div>
        ) : (
          menuNavData.map((menu) => {
            const isOpen = openMenuIds.has(menu.id)
            const currentSet = filters[menu.field] ?? new Set<string>()

            return (
              <div className="scl--anb-conduc-item" key={menu.id}>
                <button className='scl--anb-bo-ite-con' onClick={() => onClickNavMenu(menu.id)}>
                  <div className="scl--abic-ea-na">{menu.title}</div>
                  <div className="scl--abic-ic-ea-na"><DropDownIcon /></div>
                </button>

                <div className={`scl--mop-pos-fi-mod-al ${isOpen ? 'active' : ''}`}>
                  {menu.filter.map((item, index) => {
                    const isChecked =
                      item.slug === 'all'
                        ? currentSet.size === 0
                        : currentSet.has(item.slug)

                    return (
                      <div className="scl--mpfma-bo-con-st" key={item.filter_title}>
                        <div className='scl--mbcs-in-st-con'>
                          <input
                            type="checkbox"
                            id={`menu-${menu.id}-filter-${index}`}
                            checked={isChecked}
                            onChange={() => onToggle(menu.field, item.slug)}
                            autoComplete='off'
                          />
                        </div>

                        <label
                          className={`scl--mbcs-sp-con-st ${isChecked ? "active" : ""}`}
                          htmlFor={`menu-${menu.id}-filter-${index}`}
                        >
                          <span>{item.filter_title}</span>
                        </label>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })
        )}
      </nav>
    </aside>
  )
}

export default JPSidebar